import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import httpclient from "../../service/httpclient";
import "./streaming.scss";

const INIT_RETRY_DELAYS_MS = [250, 750, 1500];

function StreamingReplay() {
  const { replayId } = useParams();
  const iframeRef = useRef(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!localStorage.token) {
      window.loginRedirectUrl =
        window.location.pathname + window.location.search;
      window.location.href = "/user/login";
      return undefined;
    }

    const iframe = iframeRef.current;
    if (!iframe) return undefined;

    let cancelled = false;
    let platformUrl = null;
    let platformUserToken = null;
    let iframeLoaded = false;
    let iframeReady = false;
    let iframeInitialized = false;
    let retryTimeouts = [];

    function clearInitRetries() {
      retryTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
      retryTimeouts = [];
    }

    function sendInit() {
      if (
        !platformUrl ||
        !platformUserToken ||
        (!iframeLoaded && !iframeReady) ||
        iframeInitialized
      ) {
        return false;
      }

      iframe.contentWindow?.postMessage(
        {
          type: "init",
          userToken: platformUserToken,
          theme: "icdesign",
          language: "zh",
        },
        platformUrl
      );
      return true;
    }

    function startInitHandshake() {
      clearInitRetries();
      if (!sendInit()) return;

      retryTimeouts = INIT_RETRY_DELAYS_MS.map((delay) =>
        window.setTimeout(() => {
          sendInit();
        }, delay)
      );
    }

    function handleLoad() {
      if (!platformUrl || !iframe.src.startsWith(platformUrl + "/")) return;

      iframeLoaded = true;
      iframeInitialized = false;
      setLoading(false);
      startInitHandshake();
    }

    function handleMessage(event) {
      if (
        !platformUrl ||
        event.origin !== platformUrl ||
        event.source !== iframe.contentWindow
      ) {
        return;
      }

      if (event.data?.type === "viewer:ready") {
        iframeReady = true;
        iframeInitialized = false;
        startInitHandshake();
      }

      if (event.data?.type === "viewer:initialized") {
        iframeInitialized = true;
        clearInitRetries();
      }
    }

    function handlePageShow(event) {
      if (!event.persisted) return;

      iframeInitialized = false;
      startInitHandshake();
    }

    setLoading(true);
    iframe.addEventListener("load", handleLoad);
    window.addEventListener("message", handleMessage);
    window.addEventListener("pageshow", handlePageShow);

    httpclient
      .getStreamingPlatformToken()
      .then((data) => {
        if (cancelled) return;
        if (!data || !data.token || !data.platform_url) {
          setLoading(false);
          return;
        }

        platformUrl = data.platform_url;
        platformUserToken = data.token;
        iframeLoaded = false;
        iframeReady = false;
        iframeInitialized = false;
        clearInitRetries();
        iframe.src = platformUrl + "/viewer/replay/" + replayId;
      })
      .catch((err) => {
        if (cancelled) return;
        setLoading(false);
        console.error("Failed to get streaming platform token:", err);
      });

    return () => {
      cancelled = true;
      clearInitRetries();
      iframe.removeEventListener("load", handleLoad);
      window.removeEventListener("message", handleMessage);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [replayId]);

  return (
    <div className="streaming-page">
      {loading && <div className="streaming-loading">加载中...</div>}
      <iframe
        ref={iframeRef}
        className="streaming-iframe"
        allow="autoplay; fullscreen"
        title="Streaming replay"
        aria-label="Streaming replay"
      />
    </div>
  );
}

export default StreamingReplay;
